/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import React, { useCallback, useMemo, useState } from "react";
import { BEM, T, useTranslate } from "@adapptio/lib-components";
import { ISchemaComponentModel } from "@adapptio/lib-blueprint";
import { useBlueprintModel } from "../../hooks/useBlueprintModel";
import { InvalidModelNode } from "./InvalidModelNode";
import { PanelNote } from "../Panel";
import { termsEditorInspector } from "../../../terms/inspector";
import { Icon, ICON_SIZE } from "../Icon";
import { ToolbarButton } from "../Toolbar/ToolbarButton";
import { ToolbarCommandButton } from "../Toolbar/ToolbarCommandButton";
import { TInspectorControlComponent } from "./InspectorTypes";
import { Tabs, TABS_STYLE } from "../Tabs";
import { TABBED_CONTAINER_TYPE, TabbedContainer } from "../TabbedContainer";
import { InspectEventMap } from "./InspectEventMap";
import { Inspector } from "../../../inspector";
import { InspectorTitle } from "./InspectorTitle";
import { REFERENCE_TYPE } from "../../../modules/Core/ReferenceGuide/ReferenceGuideTypes";
import { useCommand } from "../../hooks";

export const InspectComponent: TInspectorControlComponent<ISchemaComponentModel> = ({ model: _model }) => {
	const { model } = useBlueprintModel(_model, "component");
	const t = useTranslate();

	const [ activeTab, setActiveTab ] = useState("props");

	const tabs = useMemo(
		() => [
			{
				id: "props",
				icon: "mdi/tune-variant",
				tooltip: "Options"
			},
			{
				id: "events",
				icon: "mdi/lightning-bolt",
				tooltip: "Events"
			},
			{
				id: "comments",
				icon: "mdi/comment-text",
				tooltip: "Comments"
			}
		],
		[]
	);

	const handleDelete = useCallback(() => {
		model.schema.removeSelfFromComponentList(model, true, true);
	}, [ model ]);

	if (!model) {
		return <InvalidModelNode model={_model} />;
	}

	const cmpHandler = model.componentHandler;

	if (!cmpHandler) {
		return (
			<PanelNote>
				<p>
					<T domain="editor" term={termsEditorInspector.errors.componentHasInvalidConfiguration} />
				</p>
			</PanelNote>
		);
	}

	const [ duplicateComponent ] = useCommand("duplicate.component");

	const handleDuplicate = useCallback(() => {
		duplicateComponent(model);
	}, [ model ]);

	const baseClass = BEM("edt-inspect-panel");
	baseClass.add("m-inspect-component");

	return (
		<div className={baseClass.toClassName()}>
			<div className={baseClass.element("header").toClassName()}>
				<div className={baseClass.element("header-info").toClassName()}>
					<span className={baseClass.element("header-info-icon").toClassName()}>
						<Icon name={cmpHandler.icon} size={ICON_SIZE.SMALL} />
					</span>
					<span className={baseClass.element("header-info-name").toClassName()}>
						<T domain="editor" term={cmpHandler.label} />
					</span>
					<ToolbarButton
						icon="mdi/content-copy"
						tooltip={t("editor", termsEditorInspector.component.duplicate)}
						onClick={handleDuplicate}
					/>
					<ToolbarButton
						icon="mdi/delete"
						tooltip={t("editor", termsEditorInspector.component.delete)}
						onClick={handleDelete}
					/>
					<ToolbarCommandButton
						icon="mdi/help-circle"
						tooltip={t("editor", termsEditorInspector.component.openDocumentation)}
						commandId="referenceGuide.open"
						commandArgs={[
							{
								name: cmpHandler.name,
								type: REFERENCE_TYPE.COMPONETS
							}
						]}
					/>
				</div>

				<InspectorTitle model={model.id} />
			</div>
			<Tabs
				className={baseClass.element("main-tabs").toClassName()}
				items={tabs}
				selectedId={activeTab}
				onSelect={setActiveTab}
				style={TABS_STYLE.COMPACT}
			/>
			<TabbedContainer
				selectedId={activeTab}
				style={TABBED_CONTAINER_TYPE.SLIDE}
				className={baseClass.element("content").toClassName()}
				items={[
					{
						id: "props",
						render: () => (
							<div className={baseClass.element("content-tab").toClassName()}>
								<Inspector model={model.props} />
								{!!Object.keys(model.inheritedProps?.props).length && (
									<Inspector
										model={model.inheritedProps}
										label={t("editor", "Layout")}
										group
									/>
								)}
								<Inspector label="Visibility" group>
									<Inspector model={model.visible} />
									<Inspector model={model.display} />
								</Inspector>
							</div>
						)
					},
					{
						id: "events",
						render: () => (
							<div className={baseClass.element("content-tab").toClassName()}>
								<InspectEventMap model={model.events} depth={0} path={[ "$events" ]} />
							</div>
						)
					},
					{
						id: "comments",
						render: () => (
							<div className={baseClass.element("content-tab").toClassName()}>
								<Inspector model={model.comments} />
							</div>
						)
					}
				]}
			/>
		</div>
	);
};
